import React, {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {
	ComponentUtils,
	DetailActions,
	notification,
	post,
	SwEmbeddedDetail,
	SwExitButton,
	SwForm,
	SwList,
	SwProgressBar
} from 'simplifyworks-core-ui';
import i18n from 'i18next';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {getOrganOfferRecipientsInitialValue} from '../../rest';
import {
	CountryWithFlag,
	IndeterminateCheckbox,
	OrganizationsList,
	SelectedOrganizationsList
} from '../renderers/renderers';
import {useOrganOfferHandlers} from '../../hooks/useOrganOfferHandlers';
import {FaEye} from 'react-icons/fa';
import NotificationOrMessagePreviewModal from './NotificationOrMessagePreviewModal';
import {getElementAfter} from '../../utils';

const NotificationModal = (props) => {
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [previewModalData, setPreviewModalData] = useState(null);
	const [recipientsInitialValue, setRecipientsInitialValue] = useState(null);

	const changeSelectedOrganizationsValue = (value) => {
		props.actions.detail.changeValue(
			'/api/foedus/organ-offer/notification/new',
			'DEFAULT',
			'selectedOrganizations',
			value
		);
	};

	const {
		onCheckedAllChange,
		onCheckedAllGlobalGroupsChange,
		onCheckedAllPrivateGroupsChange,
		onCountryCheckedChange,
		onOrganizationCheckedChange,
		onGlobalGroupCheckedChange,
		onPrivateGroupCheckedChange,
		getSelectedOrganizationsInCountryCount,
		getTotalOrganizationsInCountryCount,
		getSelectedOrganizationsInGlobalGroupCount,
		getTotalOrganizationsInGlobalGroupCount,
		getSelectedOrganizationsInPrivateGroupCount,
		getTotalOrganizationsInPrivateGroupCount
	} = useOrganOfferHandlers({
		newOrganOfferFormData: props?.notificationFormData,
		changeSelectedOrganizationsValue,
		loggedUserPrimaryFoedusRole: props?.loggedUserPrimaryFoedusRole
	});

	useEffect(() => {
		if (props.show) {
			getOrganOfferRecipientsInitialValue(
				{entryId: props?.entryId},
				(res) => {
					setRecipientsInitialValue({
						entryId: props?.entryId,
						definitionId: props?.definitionId,
						selectedOrganizations: res?.selectedOrganizationsDefault,
						notificationGroups: {
							countries: res?.countries?.resources,
							organizations: res?.organizations?.resources,
							globalGroups: res?.globalGroups,
							privateGroups: res?.privateGroups
						}
					});
				},
				(err) => {
					notification.showLocalizedMessage({
						level: 'error',
						titleKey: 'unexpected-error',
						messageKey: err.data.messageKey
					});
				}
			);
		}
	}, [props.show]);

	const confirmModal = () => {
		post(
			'/api/foedus/organ-offer/create-notification',
			{
				userNotificationContent: props.notificationFormData.body,
				emails: previewModalData.recipientEmails,
				selectedOrganizations: props.notificationFormData.selectedOrganizations,
				entryId: props.entryId
			},
			() => {
				notification.showLocalizedMessage({
					level: 'success',
					autoDismiss: 10,
					titleKey: 'foedus-notification-create-success-title',
					messageKey: 'foedus-notification-create-success-message'
				});

				props.actions.detail.discard('/api/foedus/organ-offer/notification/new');

				setShowPreviewModal(false);
				setRecipientsInitialValue(null);

				props.close();
				props?.actions?.detail?.load(
					'/api/ood/entries/' + props?.entryId,
					'history'
				);
			},
			(err) => {
				notification.showLocalizedMessage({
					level: 'error',
					autoDismiss: 0,
					messageKey: err.data.messageKey
				});
			}
		);
	};

	const preview = () => {
		post(
			'/api/foedus/organ-offer/notification-recipients',
			props?.notificationFormData?.selectedOrganizations ?? [],
			(recipientEmailsResponse) => {
				post(
					'/api/foedus/organ-offer/' + props.entryId + '/preview',
					{
						body: props.notificationFormData.body,
						recipients: []
					},
					(response) => {

						if (recipientEmailsResponse?.length === 0) {
							notification.showLocalizedMessage({
								level: 'error',
								titleKey: 'foedus-notification-no-recipients-title',
								messageKey: 'foedus-notification-no-recipients-message',
								autoDismiss: 0
							});
						} else {
							setPreviewModalData({
								...response,
								recipientEmails: recipientEmailsResponse
							});
							setShowPreviewModal(true);
						}
					},
					(err) => {
						notification.showLocalizedMessage({
							level: 'error',
							autoDismiss: 0,
							messageKey: err.data.messageKey
						});
					}
				);
			},
			(err) => {
				notification.showLocalizedMessage({
					level: 'error',
					autoDismiss: 0,
					messageKey: err.data.messageKey
				});
			}
		);
	};

	return (
		<>
			{showPreviewModal &&
				<NotificationOrMessagePreviewModal
					show={showPreviewModal}
					data={previewModalData}
					title={i18n.t('foedus-notification-preview')}
					confirmModalFn={confirmModal}
					closeModalFn={() => setShowPreviewModal(false)}
				/>
			}

			<Modal
				show={props.show}
				onHide={() => props.close()}
				size={'xl'}
			>
				<Modal.Header closeButton>
					<Modal.Title style={{width: 'auto'}}>
						{i18n.t('foedus-notification-title')}
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{!recipientsInitialValue
						? <SwProgressBar centered size="lg" />
						: (
							<SwEmbeddedDetail
								name='notification'
								rest='/api/foedus/organ-offer/notification'
								restId={'new'}
								tabName={''}
								hideButtons
								newCallback={(actionResult, id, errorResponse) => {
									if (!errorResponse) {
										props.close();
										window.location.reload();
									}
								}}
							>
								<SwForm
									name='notification'
									rest={''}
									labelKey='foedus-organ-offer-info'
									initialValue={recipientsInitialValue}
								>
									<SwForm.Section type='collapsible-panel' labelKey='foedus-notified-organizations' defaultExpanded>
										<SwForm.Section>
											<SwForm.Section type='tabs' labelKey='foedus-countries'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.countries'
													label=' '
												>

													<SwForm.EmbeddablesList
														name='countries'
													>
														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allCountriesSelected = props?.notificationFormData?.notificationGroups?.countries?.every(country =>
																		getSelectedOrganizationsInCountryCount(country) === getTotalOrganizationsInCountryCount(country));
																	const someCountriesSelected = props?.notificationFormData?.notificationGroups?.countries?.some(country =>
																		getSelectedOrganizationsInCountryCount(country) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allCountriesSelected}
																			indeterminate={someCountriesSelected && !allCountriesSelected}
																			onChange={onCheckedAllChange}
																		/>
																	);
																}}
																render={(resource) => {
																	const allOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) === getTotalOrganizationsInCountryCount(resource) && getTotalOrganizationsInCountryCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(e) => onCountryCheckedChange(resource, e.target.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='country'
																labelKey='foedus-country'
																render={resource => {
																	return (
																		<div className="d-flex align-items-center">
																			<CountryWithFlag
																				code={resource?.code}
																				name={resource?.nameEng ?? resource?.name}
																			/>
																			&nbsp;{getSelectedOrganizationsInCountryCount(resource)} / {getTotalOrganizationsInCountryCount(resource)}
																		</div>

																	);
																}}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => {
																	return (
																		<div className="d-flex align-items-center">
																			{props?.notificationFormData?.selectedOrganizations
																				?.filter(so => so?.organization?.code === resource?.code)
																				?.map((o, index, array) => o?.name + (index < array.length - 1 ? ', ' : ''))}
																		</div>
																	);
																}}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='core-organizations'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.organizations'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='organizations'
														defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => (
																	<Form.Check
																		checked={props?.notificationFormData?.selectedOrganizations?.length === props?.notificationFormData?.notificationGroups?.organizations?.length}
																		onChange={onCheckedAllChange}
																	/>
																)}
																render={resource => {
																	return (
																		<Form.Check
																			checked={props?.notificationFormData?.selectedOrganizations?.some(so => so?.code === resource?.code)}
																			onChange={e => onOrganizationCheckedChange(resource, e?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='country'
																labelKey='foedus-country'
																render={resource => {
																	return (
																		<>
																			<div className="d-flex align-items-center">
																				<CountryWithFlag
																					code={resource?.organization?.code}
																					name={resource?.organization?.nameEng ?? resource?.organization?.name}
																				/>
																			</div>
																		</>
																	);
																}}
															/>

															<SwList.Custom
																property='organization'
																labelKey='core-organization'
																render={resource => resource?.nameEng ?? resource?.name}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>
												</SwForm.Embeddables>

											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='foedus-global-groups'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.globalGroups'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='globalGroups'
														defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allGlobalGroupsSelected = props?.notificationFormData?.notificationGroups?.globalGroups?.every(globalGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(globalGroup) === getTotalOrganizationsInGlobalGroupCount(globalGroup));
																	const someGlobalGroupSelected = props?.notificationFormData?.notificationGroups?.globalGroups?.some(globalGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(globalGroup) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allGlobalGroupsSelected}
																			indeterminate={someGlobalGroupSelected && !allGlobalGroupsSelected}
																			onChange={onCheckedAllGlobalGroupsChange}
																		/>
																	);
																}}
																render={resource => {
																	const allOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) === getTotalOrganizationsInGlobalGroupCount(resource) && getTotalOrganizationsInGlobalGroupCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(event) => onGlobalGroupCheckedChange(resource, event?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='name'
																labelKey='foedus-name'
																render={resource => (
																	<div className="d-flex align-items-center">
																		{resource?.name}
																		&nbsp;{getSelectedOrganizationsInGlobalGroupCount(resource)} / {getTotalOrganizationsInGlobalGroupCount(resource)}
																	</div>
																)}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => <OrganizationsList
																	organizations={resource?.organizations}/>}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='foedus-private-groups'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.privateGroups'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='privateGroups'
														defaultOrdering={[{property: 'organization.name', order: 'asc'}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allPrivateGroupsSelected = props?.notificationFormData?.notificationGroups?.privateGroups?.every(privateGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(privateGroup) === getTotalOrganizationsInGlobalGroupCount(privateGroup));
																	const somePrivateGroupSelected = props?.notificationFormData?.notificationGroups?.privateGroups?.some(privateGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(privateGroup) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allPrivateGroupsSelected}
																			indeterminate={somePrivateGroupSelected && !allPrivateGroupsSelected}
																			onChange={onCheckedAllPrivateGroupsChange}
																		/>
																	);
																}}
																render={resource => {
																	const allOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) === getTotalOrganizationsInPrivateGroupCount(resource) && getTotalOrganizationsInPrivateGroupCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(event) => onPrivateGroupCheckedChange(resource, event?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='name'
																labelKey='foedus-name'
																render={resource => (
																	<div className="d-flex align-items-center">
																		{resource?.name}
																		&nbsp;{getSelectedOrganizationsInPrivateGroupCount(resource)} / {getTotalOrganizationsInPrivateGroupCount(resource)}
																	</div>
																)}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => <OrganizationsList
																	organizations={resource?.organizations}/>}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>
										</SwForm.Section>

										<SwForm.Section>
											<SwForm.Custom
												property='selectedOrganizations'
												labelKey='foedus-selected-organizations'
												render={(resource) => <SelectedOrganizationsList
													selectedOrganizations={resource?.selectedOrganizations}/>}
											/>
										</SwForm.Section>
									</SwForm.Section>

									<SwForm.Section>
										<SwForm.String
											type='area'
											rows={10}
											size={12}
											property='body'
											labelKey='foedus-notification-body'
											validations='required;length(2000)'
										/>
									</SwForm.Section>

								</SwForm>
							</SwEmbeddedDetail>
						)}
				</Modal.Body>

				<Modal.Footer>
					<SwExitButton onClick={() => {
						setShowPreviewModal(false);
						props.close();
					}}/>
					<Button
						variant='primary'
						onClick={() => preview()}
						disabled={(!recipientsInitialValue || !props?.notificationFormData?.body || props?.notificationFormData?.body?.length > 2000)}
					>
						<FaEye/>{' ' + i18n.t('foedus-notification-previewBtn')}
					</Button>
				</Modal.Footer>
			</Modal>
		</>

	);
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		detail: bindActionCreators(DetailActions, dispatch)
	}
});

const mapStateToProps = (state, ownProps) => {
	const notificationFormData = ComponentUtils.getFormData(state, '/api/foedus/organ-offer/notification/new', 'DEFAULT');
	const pathSegments = state?.router?.location?.pathname?.split('/') || [];

	return {
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole,
		notificationFormData,
		entryId: getElementAfter(pathSegments, 'entries'),
		definitionId: getElementAfter(pathSegments, 'definition')
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationModal);
